import axios from 'axios';
import { Ref, useEffect, useRef } from 'react';
import {
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  UseQueryOptions,
} from 'react-query';

// make all nullable properties on a type optional
// ty stackoverflow: https://stackoverflow.com/questions/54713272/how-can-i-make-a-partialt-but-only-for-nullable-fields
export type NullablePartial<
  T,
  NK extends keyof T = {
    [K in keyof T]: null extends T[K] ? K : never;
  }[keyof T],
  NP = Partial<Pick<T, NK>> & Pick<T, Exclude<keyof T, NK>>
> = { [K in keyof NP]: NP[K] };

export const makeAuthenticatedRequest = async <Query extends QueryType>(
  url: string,
  route: string,
  body: NullablePartial<Query['params']>,
  getAuth: () => Promise<string> | null
): Promise<Query['result']> => {
  const token = await getAuth();
  const { data: result } = await axios.post(url + '/' + route, body, {
    headers: {
      authorization: token ? `Bearer ${token}` : null,
    },
  });
  return result;
};

export const unauthenticatedRequest = async <Query extends QueryType>(
  url: string,
  route: string,
  body: NullablePartial<Query['params']>
): Promise<Query['result']> => {
  const { data: result } = await axios.post(url + '/' + route, body, {});
  return result;
};
export interface QueryType {
  params: any;
  result: any;
}

export const updateQueryCache = <Type>(
  queryClient: QueryClient,
  key: QueryKey,
  replace: Partial<Type>
) => {
  queryClient.setQueriesData<Type[]>(key, (oldData) => {
    if (oldData) {
      const newData = oldData.map((item) => {
        return { ...item, ...replace };
      });
      return newData;
    }
    return [];
  });
};

export const deleteQueryCache = <Type>(
  queryClient: QueryClient,
  key: QueryKey,
  deleteOn: Partial<Type>
) => {
  queryClient.invalidateQueries(key);
  // queryClient.setQueriesData<Type[]>(key, (oldData) => {
  //   if (oldData) {
  //     const newData = oldData.filter((item) => {
  //       for (let i of Object.keys(deleteOn)) {
  //         if (item[i as keyof Type] !== deleteOn[i as keyof Type]) {
  //           return false;
  //         }
  //       }
  //       return true;
  //     });
  //     return newData;
  //   }
  //   return [];
  // });
};

export const addQueryCache = <Type>(
  queryClient: QueryClient,
  key: QueryKey,
  newContent: Type
) => {
  queryClient.setQueriesData<Type[]>(key, (oldData: Type[] | undefined) => {
    if (oldData) {
      const newData = [newContent, ...oldData];
      return newData;
    }
    return [];
  });
};

export const codeGenFactory = (config: {
  getAuth: () => Promise<string> | null;
  url: string;
}) => {
  const { getAuth, url } = config;

  const fetch = <Query extends QueryType>(
    route: string,
    skipAuth?: boolean
  ): ((
    body: NullablePartial<Query['params']>
  ) => Promise<Query['result'][]>) => {
    return async (body: NullablePartial<Query['params']>) => {
      const result = skipAuth
        ? unauthenticatedRequest(url, route, body)
        : makeAuthenticatedRequest(url, route, body, getAuth);
      return result;
    };
  };

  const useMutationHook = <Query extends QueryType>(config: {
    route: string;
  }) => {
    const { route } = config;
    return useMutation((params: NullablePartial<Query['params']>) => {
      return fetch<Query>(route)(params);
    });
  };

  const useFetchHook = <Query extends QueryType>(
    queryKey: [string, NullablePartial<Query['params']>],
    config: UseQueryOptions<Query['result'][], undefined> & {
      skipAuth?: boolean;
    }
  ) => {
    const obj = useQuery<Query['result'][], undefined>(
      queryKey,
      () => {
        return fetch<Query>(queryKey[0], config.skipAuth)(queryKey[1]);
      },
      config
    );
    return obj;
  };

  const useInfiniteFetchHook = <Query extends QueryType>(
    queryKey: [string, NullablePartial<Query['params']>],

    config: Omit<
      UseInfiniteQueryOptions<
        Query['result'][],
        unknown,
        Query['result'][],
        [string, NullablePartial<Query['params']>]
      >,
      'initialData'
    > & {
      paginateOn: keyof NullablePartial<Query['params']>;
      // override property so we can make it fit "infinite page" after the fact
      initialData?: Query['result'][];
    }
  ) => {
    const q = useInfiniteQuery<Query['result'][]>(
      queryKey,
      ({ queryKey: pQueryKey, pageParam = null }) => {
        const p = {
          ...(pQueryKey[1] as NullablePartial<Query['params']>),
          [config.paginateOn]: pageParam,
        };
        return fetch<Query>(queryKey[0])(p);
      },
      {
        getNextPageParam: (lastPage: Query['result'][]) => {
          const nextPageParam =
            lastPage?.[lastPage.length - 1]?.[config.paginateOn];
          return nextPageParam;
        },
        initialData: config.initialData
          ? {
              pages: [config.initialData],
              pageParams: [queryKey[1][config.paginateOn]],
            }
          : undefined,
        // keepPreviousData,
        cacheTime: Infinity,
      }
    );
    return q;
  };

  const usePaginatedQuery = <Query extends QueryType>(
    queryKey: [string, NullablePartial<Query['params']>],
    config: Omit<
      UseInfiniteQueryOptions<
        Query['result'][],
        unknown,
        Query['result'][],
        [string, NullablePartial<Query['params']>]
      >,
      'initialData'
    > & {
      paginateOn: keyof NullablePartial<Query['params']>;
      existingRef?: React.Ref<HTMLDivElement>;
      // override property so we can make it fit "infinite page" after the fact
      initialData?: Query['result'][];
    }
  ) => {
    const InfiniteFetchHookResults = useInfiniteFetchHook(queryKey, config);
    let containerRef: Ref<any> = null;
    if (!config?.existingRef) {
      containerRef = useRef(null);
    }

    const onScroll = (e: React.UIEvent<HTMLElement>): void => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const menu = containerRef?.current || config?.existingRef?.current;
      const scrollToBottom =
        menu.scrollHeight - menu.offsetHeight - menu.scrollTop;
      if (
        scrollToBottom < 150 &&
        !InfiniteFetchHookResults.isFetchingNextPage
      ) {
        console.log('log ON SCROLL');
        InfiniteFetchHookResults.fetchNextPage();
      }
    };
    useEffect(() => {
      if (config?.existingRef) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        const scrollWrapper = config?.existingRef?.current;
        scrollWrapper?.addEventListener('scroll', onScroll);
        return () => {
          scrollWrapper?.removeEventListener('scroll', onScroll);
        };
      }
    }, []);

    return {
      ...InfiniteFetchHookResults,
      onScroll,
      containerRef,
    };
  };
  // return our factory-made object
  return {
    fetch,
    useFetchHook,
    useMutationHook,
    useInfiniteFetchHook,
    usePaginatedQuery,
  };
};
